import React from 'react';
import { useThemeClasses } from '../lib/themes/utils';
import { Navigation } from './common/Navigation';
import { Footer } from './common/Footer';

export const PersonalizedPage: React.FC = () => {
  const theme = useThemeClasses();

  return (
    <div className={`min-h-screen ${theme.backgrounds.gradient}`}>
      <Navigation />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary-50 via-white to-primary-100">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary-100/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-200/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          <svg className="absolute top-32 left-20 w-32 h-32 text-primary-200 opacity-20 animate-float" viewBox="0 0 100 100">
            <path d="M50 10 Q 20 40 25 70 T 50 90 Q 80 60 75 30 Z" fill="currentColor" />
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-white/50 backdrop-blur-sm rounded-full">
              <span className="text-primary-600 text-sm font-medium">1:1 Holistic Nutrition</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl font-light text-gray-900 mb-6 tracking-tight">
              Your <span className="italic text-primary-600">Personalized</span> Path
              <br />
              <span className="font-normal">to Whole Body Healing</span>
            </h1>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-12">
              Experience transformative healing through personalized nutrition counseling, 
              comprehensive health assessments, and custom protocols designed specifically 
              for your unique biochemistry and lifestyle.
            </p>

            <a 
              href="/intake" 
              className="inline-flex items-center gap-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-8 py-4 rounded-xl font-medium text-lg hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg hover:shadow-xl"
            >
              <span>Begin Your Intake Assessment</span>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* What to Expect Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">
              What to Expect
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A comprehensive, compassionate approach to understanding and healing your unique health challenges
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-6">
                <span className="text-3xl">📋</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Comprehensive Assessment
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Begin with our in-depth intake form covering your health history, symptoms, 
                lifestyle, and goals. This thorough assessment helps us understand your whole story.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-6">
                <span className="text-3xl">🔬</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Root Cause Analysis
              </h3>
              <p className="text-gray-600 leading-relaxed">
                We look beyond symptoms to identify underlying imbalances in your gut health, 
                hormones, nutrient status, and metabolic function using functional nutrition principles.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-6">
                <span className="text-3xl">🌱</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Custom Protocols
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Receive personalized nutrition, supplement, and lifestyle recommendations 
                tailored to your specific needs, preferences, and healing goals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 bg-gradient-to-b from-white to-primary-50/30">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">
              Your Healing Journey
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A structured approach to sustainable health transformation
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-12">
              {/* Step 1 */}
              <div className="flex gap-8 items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-primary-500 text-white rounded-full flex items-center justify-center font-semibold">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Complete Your Intake Assessment
                  </h3>
                  <p className="text-gray-600">
                    Fill out our comprehensive health questionnaire at your own pace. 
                    This detailed assessment helps us understand your health history, 
                    current symptoms, and wellness goals.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-8 items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-primary-500 text-white rounded-full flex items-center justify-center font-semibold">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Initial Consultation
                  </h3>
                  <p className="text-gray-600">
                    We'll review your assessment together in a 90-minute consultation. 
                    This is your time to share your story, ask questions, and collaborate 
                    on your healing priorities.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-8 items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-primary-500 text-white rounded-full flex items-center justify-center font-semibold">
                  3
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Receive Your Custom Protocol
                  </h3>
                  <p className="text-gray-600">
                    Within 48 hours, you'll receive your personalized nutrition and lifestyle 
                    protocol, including meal plans, supplement recommendations, and healing practices.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-8 items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-primary-500 text-white rounded-full flex items-center justify-center font-semibold">
                  4
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Ongoing Support & Adjustments
                  </h3>
                  <p className="text-gray-600">
                    Regular follow-up sessions ensure your protocol evolves with your progress. 
                    We'll make adjustments based on your response and changing needs.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">
              Investment in Your Health
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the support level that aligns with your healing journey
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* REBOOT Package */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-br from-primary-400 to-primary-600 p-8 text-white">
                <h3 className="text-2xl font-semibold mb-2">REBOOT</h3>
                <p className="text-primary-100">1-Month Reset Program</p>
              </div>
              <div className="p-8">
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Comprehensive intake assessment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">90-minute initial consultation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Custom nutrition protocol</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">1 follow-up session</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Email support between sessions</span>
                  </li>
                </ul>
                <div className="text-center">
                  <p className="text-3xl font-bold text-gray-900 mb-4">$497</p>
                  <a 
                    href="/intake" 
                    className="inline-block w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:from-primary-600 hover:to-primary-700 transition-all"
                  >
                    Get Started
                  </a>
                </div>
              </div>
            </div>

            {/* REFRESH & REVITALIZE Package */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-primary-500">
              <div className="bg-gradient-to-br from-primary-500 to-primary-700 p-8 text-white relative">
                <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                  Most Popular
                </div>
                <h3 className="text-2xl font-semibold mb-2">REFRESH & REVITALIZE</h3>
                <p className="text-primary-100">3-Month Transformation</p>
              </div>
              <div className="p-8">
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Everything in REBOOT, plus:</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">5 additional follow-up sessions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Advanced functional testing guidance</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Cycle syncing protocols</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Priority email & messaging support</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Access to client resource library</span>
                  </li>
                </ul>
                <div className="text-center">
                  <p className="text-3xl font-bold text-gray-900 mb-4">$1,497</p>
                  <a 
                    href="/intake" 
                    className="inline-block w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:from-primary-600 hover:to-primary-700 transition-all"
                  >
                    Get Started
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-b from-white to-primary-50/30">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-light text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            <details className="bg-white rounded-xl shadow-md overflow-hidden group">
              <summary className="p-6 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 transition-colors">
                <span className="text-lg font-medium text-gray-900">
                  How is holistic nutrition different from regular nutrition counseling?
                </span>
                <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="px-6 pb-6 text-gray-600">
                Holistic nutrition looks at your whole person - not just what you eat. We consider your stress levels, 
                sleep quality, emotional health, environmental factors, and biochemical individuality to create a 
                comprehensive healing plan that addresses root causes, not just symptoms.
              </div>
            </details>

            <details className="bg-white rounded-xl shadow-md overflow-hidden group">
              <summary className="p-6 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 transition-colors">
                <span className="text-lg font-medium text-gray-900">
                  Do I need to get lab tests before starting?
                </span>
                <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="px-6 pb-6 text-gray-600">
                No, you don't need labs to begin. We can work with any recent lab work you have, and I'll guide you 
                on which functional tests might be helpful based on your symptoms and goals. Many clients see significant 
                improvements without any testing.
              </div>
            </details>

            <details className="bg-white rounded-xl shadow-md overflow-hidden group">
              <summary className="p-6 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 transition-colors">
                <span className="text-lg font-medium text-gray-900">
                  How quickly will I see results?
                </span>
                <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="px-6 pb-6 text-gray-600">
                Many clients notice improvements in energy and digestion within the first 2-3 weeks. Deeper healing 
                of hormonal imbalances, gut health, and metabolic issues typically takes 3-6 months. Remember, we're 
                addressing root causes for lasting change, not quick fixes.
              </div>
            </details>

            <details className="bg-white rounded-xl shadow-md overflow-hidden group">
              <summary className="p-6 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 transition-colors">
                <span className="text-lg font-medium text-gray-900">
                  What if I have dietary restrictions or preferences?
                </span>
                <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="px-6 pb-6 text-gray-600">
                Your protocols are completely personalized to your needs, preferences, and lifestyle. Whether you're 
                vegan, have food allergies, or follow specific dietary practices, we'll create a plan that works for 
                YOU. No one-size-fits-all approaches here.
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="text-3xl font-light text-gray-900 mb-6">
            Ready to Transform Your Health?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Your journey to vibrant health begins with a single step. 
            Complete your intake assessment today.
          </p>
          <a 
            href="/intake" 
            className="inline-flex items-center gap-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-8 py-4 rounded-xl font-medium text-lg hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg hover:shadow-xl"
          >
            <span>Start Your Intake Assessment</span>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};